import { motion } from 'framer-motion';
import { 
  MapPin, Phone, EnvelopeSimple, WhatsappLogo, 
  YoutubeLogo, SpotifyLogo, LinkedinLogo, XLogo, 
  InstagramLogo, TiktokLogo, Globe, ArrowUpRight 
} from '@phosphor-icons/react';

const Contact = () => {
  const contactInfo = [
    {
      icon: MapPin,
      label: 'Ubicación',
      value: 'C/ Adriano 32-5º, 41001 Sevilla'
    },
    {
      icon: Phone,
      label: 'Teléfono',
      value: '954 564 602 / 615 175 823',
      link: 'tel:+34954564602'
    },
    {
      icon: WhatsappLogo,
      label: 'WhatsApp',
      value: '954 564 602',
      link: 'https://wa.me/34954564602'
    },
    {
      icon: EnvelopeSimple,
      label: 'Email',
      value: 'info@tecnoabogado.com',
      link: 'mailto:info@tecnoabogado.com'
    }
  ];

  const websites = [
    { name: 'Vilaplana Abogados', url: 'https://www.vilaplanaabogados.com', desc: 'Despacho profesional' },
    { name: 'Despido.net', url: 'https://despido.net/', desc: 'Derecho laboral' },
    { name: 'Habilidades Abogados', url: 'https://habilidadesabogados.es/', desc: 'Formación' },
    { name: 'Jurisciberia', url: 'https://www.jurisciberia.com', desc: 'Ciberseguridad' },
    { name: 'Tecnoabogado', url: 'https://www.tecnoabogado.com', desc: 'Blog jurídico' },
    { name: 'IABogacia', url: 'https://iabogacia.com', desc: 'IA jurídica' }
  ];

  const socials = [
    { icon: YoutubeLogo, name: 'YouTube', handle: '@tecnoabogado', url: 'https://www.youtube.com/@tecnoabogado', color: 'hover:text-red-500' },
    { icon: SpotifyLogo, name: 'Spotify', handle: 'Podcast', url: 'https://open.spotify.com/show/2XqXeI0Q1KpsMQbo4c2NZf', color: 'hover:text-green-500' },
    { icon: LinkedinLogo, name: 'LinkedIn', handle: 'vilaplanajoseluis', url: 'https://www.linkedin.com/in/vilaplanajoseluis', color: 'hover:text-blue-400' },
    { icon: XLogo, name: 'X', handle: '@tecnoabogado', url: 'https://twitter.com/tecnoabogado', color: 'hover:text-white' },
    { icon: InstagramLogo, name: 'Instagram', handle: 'tecnoabogado', url: 'https://www.instagram.com/tecnoabogado', color: 'hover:text-pink-500' },
    { icon: TiktokLogo, name: 'TikTok', handle: '@tecnoabogado', url: 'https://www.tiktok.com/@tecnoabogado', color: 'hover:text-white' }
  ];

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: { staggerChildren: 0.1 }
    }
  };

  const itemVariants = {
    hidden: { y: 20, opacity: 0 },
    visible: { y: 0, opacity: 1, transition: { duration: 0.5 } }
  };

  return (
    <section id="contact" className="py-20 bg-[#0A0A0A] border-t border-white/10">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          variants={containerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.1 }}
        >
          {/* Section Header */}
          <motion.div variants={itemVariants} className="mb-12">
            <p className="font-mono text-xs tracking-[0.2em] uppercase text-blue-500 mb-4">
              [05] Contacto
            </p>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-medium tracking-tight text-white">
              Conecta Conmigo
            </h2>
          </motion.div>

          <div className="grid lg:grid-cols-2 gap-8 mb-12">
            {/* Contact Info */}
            <motion.div variants={itemVariants} className="space-y-4">
              {contactInfo.map((info, index) => (
                <div
                  key={index}
                  className="flex items-start gap-4 p-4 bg-[#0F0F0F] border border-white/10 card-hover"
                  data-testid={`contact-${info.label.toLowerCase()}`}
                >
                  <div className="w-10 h-10 flex items-center justify-center border border-blue-500/30 bg-blue-500/10">
                    <info.icon size={20} className="text-blue-500" weight="light" />
                  </div>
                  <div className="flex-1">
                    <p className="font-mono text-xs text-white/50 uppercase tracking-wider mb-1">
                      {info.label}
                    </p>
                    {info.link ? (
                      <a 
                        href={info.link}
                        className="text-white font-medium hover:text-blue-500 transition-colors"
                      >
                        {info.value}
                      </a>
                    ) : (
                      <p className="text-white font-medium">{info.value}</p>
                    )}
                  </div>
                </div>
              ))}
            </motion.div>

            {/* Websites */}
            <motion.div variants={itemVariants} className="p-6 bg-[#0F0F0F] border border-white/10">
              <div className="flex items-center gap-3 mb-4">
                <Globe size={20} className="text-blue-500" weight="light" />
                <h3 className="font-mono text-sm text-white/50 uppercase tracking-wider">
                  Plataformas Web
                </h3>
              </div>
              
              <div className="space-y-3">
                {websites.map((site, index) => (
                  <a
                    key={index}
                    href={site.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center justify-between p-3 bg-white/5 border border-white/5 hover:border-blue-500/30 transition-all group"
                    data-testid={`website-${site.name.toLowerCase().replace(/\s/g, '-')}`}
                  >
                    <div>
                      <p className="text-white font-medium text-sm">{site.name}</p>
                      <p className="font-mono text-xs text-white/40">{site.desc}</p>
                    </div>
                    <ArrowUpRight size={16} className="text-white/30 group-hover:text-blue-500 transition-colors" />
                  </a>
                ))}
              </div>
            </motion.div>
          </div>

          {/* Social Media */}
          <motion.div variants={itemVariants} className="mb-12">
            <div className="flex items-center gap-3 mb-6">
              <h3 className="font-mono text-sm text-white/50 uppercase tracking-wider">
                Redes y Contenido — @tecnoabogado
              </h3>
            </div>
            
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
              {socials.map((social, index) => (
                <a
                  key={index}
                  href={social.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className={`flex items-center gap-3 p-4 bg-[#0F0F0F] border border-white/10 hover:border-white/30 transition-all ${social.color}`}
                  data-testid={`social-${social.name.toLowerCase()}`}
                >
                  <social.icon size={24} weight="light" />
                  <div className="hidden sm:block">
                    <p className="text-sm font-medium">{social.name}</p>
                  </div>
                </a>
              ))}
            </div>
          </motion.div>

          {/* CTA */}
          <motion.div 
            variants={itemVariants}
            className="p-8 sm:p-12 bg-[#0F0F0F] border border-white/10 text-center"
          >
            <h3 className="text-2xl sm:text-3xl font-medium text-white mb-4">
              ¿Listo para transformar tu práctica jurídica?
            </h3>
            <p className="text-white/50 max-w-2xl mx-auto mb-6">
              Consultoría en transformación digital, IA aplicada al derecho, ciberseguridad 
              y formación especializada para despachos y profesionales.
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <a
                href="https://calendar.app.google/8cNXBV981iJPzp4W9"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center px-8 py-3 bg-blue-600 text-white font-medium hover:bg-blue-700 transition-colors"
                data-testid="cta-contactar"
              >
                Contactar Ahora
              </a>
              <a
                href="https://www.vilaplanaabogados.com"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center px-8 py-3 border border-white/20 text-white font-medium hover:border-white/40 transition-colors"
                data-testid="cta-despacho"
              >
                Visitar Despacho
              </a>
            </div>
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
};

export default Contact;
